import { AuthorizationParams, useAuth0 } from '@auth0/auth0-react';
import { usePersistedState } from '@luminovo/design-system';
import { useQueryClient } from '@tanstack/react-query';

import { clearAuthSessionStorage } from '../sessionStorage';

/**
 * Currently used only for development purposes. We're only setting this manually in the dev console.
 */
export const AUTH0_CONNECTION_TYPE_SESSION_KEY = 'luminovo.auth0.connectionType';

export enum ConnectionType {
    // These are the actual values used by Auth0. Changing these values will break the login flow.
    passwordless = 'email',
    password = 'Username-Password-Authentication',
}

function deprecated_getConnectionType(): ConnectionType | undefined {
    const connectionType = localStorage.getItem(AUTH0_CONNECTION_TYPE_SESSION_KEY);
    if (connectionType === ConnectionType.passwordless) {
        return ConnectionType.passwordless;
    }
    if (connectionType === ConnectionType.password) {
        return ConnectionType.password;
    }
    return undefined;
}

// A function: reading `window` at module load made importing `@luminovo/auth` throw during vitest collection.
export function getAuth0AuthorizationParams(): AuthorizationParams {
    return {
        audience: 'https://luminovo.ai/api',
        redirect_uri: window.location.origin + '/callback', // callback configured at https://manage.auth0.com/dashboard/eu/luminovo/applications/49APfyeNsIPNBAb2nMCUnmsh4Lu6epkM/settings
        connection: deprecated_getConnectionType(),
    };
}

function setConnectionType(connectionType: ConnectionType | string | undefined): void {
    localStorage.setItem(AUTH0_CONNECTION_TYPE_SESSION_KEY, connectionType ?? '');
}

/**
 * A single hook that encapsulates all authentication logic in Luminovo.
 *
 * This hook can be safely called from a non-authenticated context.
 *
 */
export function useAuth({ auth0OrgId }: { auth0OrgId: string | undefined }): {
    loginWithRedirect: ({
        email,
        returnTo,
        connectionType,
    }: {
        email?: string;
        returnTo: string;
        connectionType?: ConnectionType | string;
    }) => Promise<void>;
    logout: () => Promise<void>;
    isLoading: boolean;
    isAuthenticated: boolean;
    error: Error | undefined;
    email: string;
    /** Stable Auth0 subject; use for user-scoped session state, never email. */
    userSubject: string | undefined;
    getAccessTokenSilently: () => Promise<string>;
} {
    const auth0 = useAuth0();
    const [email, setEmail] = usePersistedState<string>('luminovo.email', auth0.user?.email ?? '', localStorage);
    const queryClient = useQueryClient();
    const authorizationParams: AuthorizationParams = {
        ...getAuth0AuthorizationParams(),
        login_hint: email,
        organization: auth0OrgId,
        connection_: deprecated_getConnectionType(),
    };

    return {
        getAccessTokenSilently: async () => {
            const token = await auth0.getAccessTokenSilently({ authorizationParams });
            if (token === undefined) {
                throw new Error('Auth0 returned no access token');
            }
            return token;
        },
        isLoading: auth0.isLoading,
        isAuthenticated: auth0.isAuthenticated,
        error: auth0.error,
        email,
        userSubject: auth0.user?.sub,
        loginWithRedirect: ({
            email,
            returnTo,
            connectionType,
        }: {
            email?: string;
            returnTo: string;
            connectionType?: ConnectionType | string;
        }) => {
            return auth0
                .loginWithRedirect({
                    authorizationParams: {
                        ...authorizationParams,
                        login_hint: email,
                        connection: connectionType,
                    },
                    appState: { returnTo },
                })
                .then(() => {
                    setConnectionType(connectionType);
                    setEmail(email ?? '');
                });
        },
        logout: async () => {
            clearAuthSessionStorage();
            // Clear the connection type in case this is causing any issues.
            setConnectionType(undefined);
            setEmail('');

            await auth0.logout({ logoutParams: { returnTo: window.location.origin } });
            await queryClient.invalidateQueries();
        },
    };
}
